import { isTimeOlderThanThreshold } from 'app/utils/dates';
import { CONSTANTS } from 'utils/constants';
import { sntConfig } from 'app/models/install-verification-tags/device-config/slap-and-track-config';

interface GPSData {
  latitude?: string;
  lat?: string;
  longitude?: string;
  lon?: string;
  lng?: string;
  accuracy?: number | string;
  altitude?: number | string;
}

export interface LocationData {
  location?: GPSData;
  gps?: GPSData;
  lastValidLocationTimestamp?: string;
  useLastValidLocationTimestamp?: boolean;
}

/**
 * Location model
 */
class Location {
  readonly lat: number;
  readonly lng: number;
  readonly altitude?: number | string;
  readonly accuracy?: number | string;
  readonly lastValidLocationTimestamp?: string;
  readonly useLastValidLocationTimestamp?: boolean;

  constructor(data: LocationData) {
    const gpsData = (data.location || data.gps) ?? {
      latitude: 0,
      lat: 0,
      lon: 0,
      lng: 0,
      longitude: 0,
      accuracy: 0,
      altitude: 0,
    };

    //make separate function for checking which lat or long
    const latitude = gpsData?.lat || gpsData?.latitude;
    const longitude = gpsData?.lon || gpsData?.longitude || gpsData?.lng;
    this.lat = Number(latitude);
    this.lng = Number(longitude);
    this.accuracy = gpsData?.accuracy ? gpsData.accuracy : CONSTANTS.TEXT_PLACEHOLDER;
    this.altitude = gpsData?.altitude ? gpsData.altitude : CONSTANTS.TEXT_PLACEHOLDER;
    this.lastValidLocationTimestamp = data.lastValidLocationTimestamp;
    this.useLastValidLocationTimestamp = data.useLastValidLocationTimestamp ?? false;
  }

  /**
   * Validates coordinates are non-zero and within any of the configured operating regions.
   *
   * Returns false if coordinates are zero (default/invalid) or fall outside all configured region bounds.
   * Returns true if coordinates fall within ANY of the configured valid regions.
   */
  public isValidLatLng() {
    if (this.lat === 0 || this.lng === 0) return false;

    const validRegions = sntConfig.health.location.validRegions;

    // Check if coordinates fall within ANY of the configured regions
    return validRegions.some(region => {
      const isLatitudeValid = this.lat >= region.MIN_LATITUDE && this.lat <= region.MAX_LATITUDE;
      const isLongitudeValid = this.lng >= region.MIN_LONGITUDE && this.lng <= region.MAX_LONGITUDE;
      return isLatitudeValid && isLongitudeValid;
    });
  }

  /**
   * Checks whether the last good GPS location timestamp is recent enough to be trusted in case of missing or invalid GPS data.
   *
   * Currently considers a timestamp valid if it is not older than 2 days from now.
   */
  public isValidLastLocationTimestamp() {
    const lastValidLocationTimeRange = sntConfig.health.location.lastValidLocationTimeRange;
    if (!this.useLastValidLocationTimestamp) {
      return true;
    }
    return !isTimeOlderThanThreshold(
      this.lastValidLocationTimestamp,
      lastValidLocationTimeRange,
      'days'
    );
  }

  /**
   * Shorthand call to get the {lat,lng} object
   *
   * @getter center
   */
  get center() {
    return {
      lat: this.lat,
      lng: this.lng,
    };
  }

  /**
   * Returns Latitude/Longitude fixed to two decimal places
   * as {shortenedLat, shortenedLng}
   *
   * @getter shortenedCoords
   */
  get shortenedCoords() {
    return {
      shortenedLat: this.lat.toFixed(2),
      shortenedLng: this.lng.toFixed(2),
    };
  }

  /**
   * Returns a Google Maps URL using this.lat and this.lng
   *
   * @getter googleMapsUrl
   */
  get googleMapsUrl() {
    return `${CONSTANTS.GOOGLE_MAPS_URL}${this.lat},${this.lng}`;
  }
}

export { Location };
